import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterRenderEffect,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';

let nextExpandableTextId = 0;

const ROUNDING_TOLERANCE = 1;

@Component({
  selector: 'procure-expandable-text',
  templateUrl: './expandable-text.component.html',
  styleUrl: './expandable-text.component.scss',
  host: {
    '[style.--procure-expandable-text-max-lines]': 'maxLines()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpandableText {
  readonly text = input<string | null | undefined>(null);
  readonly maxLines = input<number>(4);

  protected readonly textId = `expandable-text-${nextExpandableTextId++}`;
  protected readonly expanded = signal(false);
  protected readonly overflowing = signal(false);

  protected readonly hasText = computed(() => !!this.text()?.trim());

  protected readonly toggleLabel = computed(() =>
    this.expanded() ? 'Show less' : 'Show more',
  );

  private readonly textElement =
    viewChild<ElementRef<HTMLElement>>('textElement');

  private resizeObserver: ResizeObserver | null = null;
  private observedElement: HTMLElement | null = null;

  constructor() {
    effect(() => {
      this.text();
      this.expanded.set(false);
    });

    afterRenderEffect({
      read: () => {
        this.text();

        const element = this.textElement()?.nativeElement;

        if (!element) {
          this.overflowing.set(false);

          return;
        }

        this.observe(element);
        this.measureOverflow(element);
      },
    });

    inject(DestroyRef).onDestroy(() => this.resizeObserver?.disconnect());
  }

  protected toggle(): void {
    this.expanded.update((expanded) => !expanded);
  }

  private observe(element: HTMLElement): void {
    if (
      this.observedElement === element ||
      typeof ResizeObserver === 'undefined'
    ) {
      return;
    }

    this.resizeObserver?.disconnect();
    this.observedElement = element;

    this.resizeObserver = new ResizeObserver(() =>
      this.measureOverflow(element),
    );

    this.resizeObserver.observe(element);
  }

  private measureOverflow(element: HTMLElement): void {
    if (this.expanded()) {
      return;
    }

    this.overflowing.set(
      element.scrollHeight - element.clientHeight > ROUNDING_TOLERANCE,
    );
  }
}
